// ============================================================================
// 📁 FILE: src/modules/ticket/pages/TicketDetailPage.js
// 📆 Updated On: 2025-12-02 + 2025-12-06 (SLA Integration)
// 📝 Update Summary:
// - Added EditTicketDrawer integration
// - Added SLA integration
// - Added <SlaBlock /> UI component
// ============================================================================

import React from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

// HOOKS
import useTicketDetail from "../hooks/useTicketDetail";
import useSLA from "../hooks/useSLA";

// COMPONENTS
import TicketHeader from "../components/detail/TicketHeader";
import StatusBlock from "../components/detail/StatusBlock";
import SlaBlock from "../components/detail/SlaBlock";              // <-- NEW
import CustomFieldsBlock from "../components/detail/CustomFieldsBlock";
import CommentsBlock from "../components/detail/CommentsBlock";
import AttachmentsBlock from "../components/detail/AttachmentsBlock";
import ActivityLogBlock from "../components/detail/ActivityLogBlock";
import AssignmentDrawer from "../components/detail/AssignmentDrawer";
import GeneralInfoBlock from "../components/detail/GeneralInfoBlock";
import ClassificationBlock from "../components/detail/ClassificationBlock";
import EditTicketDrawer from "../components/detail/EditTicketDrawer";

// STYLES
import "../styles/ticketDetail.css";

export default function TicketDetailPage() {
  const { ticketId } = useParams();
  const { t } = useTranslation();

  // Load full ticket detail
  const {
    ticket,
    comments,
    attachments,
    audit,
    loading,
    error,
    addComment,
    changeStatus,
    assign,
    uploadAttachment,
    refreshAll,
  } = useTicketDetail(ticketId);

  // NEW: SLA Hook (auto refresh)
  const {
    sla,
    loading: slaLoading,
    error: slaError,
  } = useSLA(ticketId);

  // Drawer states
  const [openAssign, setOpenAssign] = React.useState(false);
  const [openEdit, setOpenEdit] = React.useState(false);

  // Loading state
  if (loading)
    return (
      <div className="tf-center-loading">
        {t("ticket.loading", "Loading...")}
      </div>
    );

  if (error || !ticket)
    return (
      <div className="tf-error">
        {t("ticket.detail.load_failed", "Failed to load ticket")}
      </div>
    );

  // Main Render
  return (
    <div className="tf-ticket-detail-container">

      <TicketHeader
        ticket={ticket}
        onOpenAssign={() => setOpenAssign(true)}
        onEditClick={() => setOpenEdit(true)}
      />

      <div className="tf-detail-panels">

        {/* LEFT PANEL */}
        <div className="tf-left-panel">

          {/* STATUS */}
          <StatusBlock
            ticket={ticket}
            onStatusChange={async ({ status }) => {
              await changeStatus({ status });
              await refreshAll();
            }}
          />

          {/* SLA BLOCK */}
          <SlaBlock sla={sla} loading={slaLoading} />

          {/* INFO */}
          <GeneralInfoBlock ticket={ticket} />
          <ClassificationBlock ticket={ticket} />

          {/* CUSTOM FIELDS */}
          <CustomFieldsBlock customFields={ticket.custom_fields} />

          {/* COMMENTS */}
          <CommentsBlock
            comments={comments}
            onAddComment={async (content) => {
              await addComment({ content });
              await refreshAll();
            }}
          />

          {/* ATTACHMENTS */}
          <AttachmentsBlock
            attachments={attachments}
            onUpload={async (formData) => {
              await uploadAttachment(formData);
              await refreshAll();
            }}
          />
        </div>

        {/* RIGHT PANEL */}
        <div className="tf-right-panel">
          <ActivityLogBlock audits={audit} loading={loading} />
        </div>

      </div>

      {/* DRAWERS */}
      <AssignmentDrawer
        open={openAssign}
        onClose={() => setOpenAssign(false)}
        ticket={ticket}
        onAssigned={async () => {
          setOpenAssign(false);
          await refreshAll();
        }}
      />

      <EditTicketDrawer
        isOpen={openEdit}
        onClose={() => setOpenEdit(false)}
        ticket={ticket}
        onUpdated={async () => {
          await refreshAll();
        }}
      />

    </div>
  );
}
